import { type GlobalShortcutsItem } from '@atlassian/global-shortcuts';

import {
	CUSTOMIZE_SIDEBAR_MENU_ITEM_TEST_ID,
	MORE_MENU_TEST_ID,
} from '../../../ui/more-menu/constants';
import {
	type SideNavClickedSideNavItemAttributesType,
	type SideNavViewedSideNavAppShortcutsSectionAttributesType,
} from '../../analytics/generated/analytics.types';
import { useAnalyticsEvents } from '../../analytics/generated/use-analytics-events';
import {
	type CustomizationDataShapes,
	type ForYouConfig,
	type HasMenuIdAndVisible,
	type ItemsConfig,
	type NavItemConfig,
	type ShortcutsConfig,
} from '../../types';

export function useNavAnalytics() {
	const { fireEvent } = useAnalyticsEvents();

	const fireViewSideNavEvent = (
		forYou: ForYouConfig,
		sideNavMenuData: CustomizationDataShapes,
		appSpecificItemsList: string[],
		isCustomizationEnabled: boolean,
		hasBottomElem: boolean,
	) => {
		const moreMenuItems = sideNavMenuData.hiddenItemMenuIds.concat(
			sideNavMenuData.hiddenShortcutMenuIds,
		);

		const globalItemsList = [
			...(!forYou.hideForYou ? [forYou.forYouMenuItemId ?? 'global.sidebar.forYou'] : []),
			...(!forYou.hideRecents ? [forYou.recentsMenuItemId ?? 'global.sidebar.recents'] : []),
			...(!forYou.hideStarred ? [forYou.starredMenuItemId ?? 'global.sidebar.starred'] : []),
			...(isCustomizationEnabled
				? [moreMenuItems.length ? MORE_MENU_TEST_ID : CUSTOMIZE_SIDEBAR_MENU_ITEM_TEST_ID]
				: []),
			...(hasBottomElem ? ['global.sidebar.bottomElem'] : []),
		];

		const appShortcutItemsList = sideNavMenuData.visibleShortcutMenuIds;

		fireEvent('ui.sideNav.viewed.sideNavMenu', {
			globalItemsCount: globalItemsList.length,
			globalItemsList,
			appSpecificItemsCount: appSpecificItemsList.length,
			appSpecificItemsList,
			appShortcutItemsCount: appShortcutItemsList.length,
			appShortcutItemsList,
			inMoreItemsCount: moreMenuItems.length,
			inMoreItemsList: moreMenuItems,
		});
	};

	const fireClickSideNavNavItemEvent = (
		id: string,
		index: number,
		items: ItemsConfig,
		globalItems: Map<string, NavItemConfig>,
		sideNavMenuData: CustomizationDataShapes,
		totalNavItemsCount: number,
		additionalGlobalItemsCount: number,
		isInMore: boolean = false,
	) => {
		const isAppSpecificItem = Object.keys(items).includes(id);
		const currentGroupItems = sideNavMenuData.visibleItemMenuIds.filter((id) =>
			isAppSpecificItem ? !globalItems.has(id) : globalItems.has(id),
		);
		fireClickSideNavItemEvent({
			navLayoutComponent: 'main',
			navItemsCount: totalNavItemsCount,
			navItemsIndex: index,
			group: isAppSpecificItem ? 'appSpecific' : 'global',
			groupItemsCount: isAppSpecificItem
				? currentGroupItems.length
				: currentGroupItems.length + additionalGlobalItemsCount, // add more menu/customise button & bottom elem,
			groupItemsIndex: currentGroupItems.findIndex((menuId) => menuId === id),
			groupItem: id,
			isInMore,
		});
	};

	const fireClickSideNavShortcutItemEvent = (
		shortcutId: string,
		index: number,
		hydratedShortcuts: ShortcutsConfig | undefined,
		sideNavMenuData: CustomizationDataShapes,
		totalNavItemsCount: number,
		growthItemsCount: number,
	) => {
		const visibleShortcutItems = sideNavMenuData.visibleShortcutMenuIds.filter(
			(id) => hydratedShortcuts?.[id],
		);
		fireClickSideNavItemEvent({
			navLayoutComponent: 'postMain',
			navItemsCount: totalNavItemsCount,
			navItemsIndex: sideNavMenuData.visibleItemMenuIds.length + index,
			group: 'appShortcut',
			groupItemsCount: visibleShortcutItems.length + growthItemsCount,
			groupItemsIndex: index,
			groupItem: shortcutId,
			isInMore: false,
		});
	};

	const fireClickSideNavMoreMenuItemEvent = (
		globalItems: Map<string, NavItemConfig>,
		sideNavMenuData: CustomizationDataShapes,
		totalNavItemsCount: number,
		additionalGlobalItemsCount: number,
		growthItemsCount: number,
	) => {
		const visibleGlobalItems = sideNavMenuData.visibleItemMenuIds.filter((id) =>
			globalItems.has(id),
		);
		fireClickSideNavItemEvent({
			navLayoutComponent: 'postMain',
			navItemsCount: totalNavItemsCount,
			navItemsIndex: sideNavMenuData.visibleMenuIds.length + growthItemsCount,
			group: 'global',
			groupItemsCount: visibleGlobalItems.length + additionalGlobalItemsCount,
			groupItemsIndex: visibleGlobalItems.length,
			groupItem: 'more',
			isInMore: false,
		});
	};

	const fireClickSideNavCustomizeSidebarItemEvent = (
		globalItems: Map<string, NavItemConfig>,
		sideNavMenuData: CustomizationDataShapes,
		totalNavItemsCount: number,
		additionalGlobalItemsCount: number,
		growthItemsCount: number,
	) => {
		const visibleGlobalItems = sideNavMenuData.visibleItemMenuIds.filter((id) =>
			globalItems.has(id),
		);
		const isInMore = !!sideNavMenuData.hiddenMenuIds.length; // is in more menu if there are any hidden items
		fireClickSideNavItemEvent({
			navLayoutComponent: 'postMain',
			navItemsCount: isInMore ? sideNavMenuData.hiddenMenuIds.length + 1 : totalNavItemsCount, // +1 for the customise sidebar item itself
			navItemsIndex: isInMore
				? sideNavMenuData.hiddenMenuIds.length
				: sideNavMenuData.visibleMenuIds.length + growthItemsCount,
			group: 'global',
			groupItemsCount: isInMore ? 1 : visibleGlobalItems.length + additionalGlobalItemsCount,
			groupItemsIndex: isInMore ? 0 : visibleGlobalItems.length,
			groupItem: CUSTOMIZE_SIDEBAR_MENU_ITEM_TEST_ID,
			isInMore,
		});
	};

	const fireClickSideNavItemEvent = (attributes: SideNavClickedSideNavItemAttributesType) =>
		fireEvent('ui.sideNav.clicked.sideNavItem', attributes);

	const fireViewSideNavSubMenuEvent = (
		id: string,
		globalItems: Map<string, NavItemConfig>,
		appSpecificItems: ItemsConfig,
		visibleItemMenuIds: string[],
		subItemsCount?: number,
		isInMore: boolean = false,
	) => {
		const isGlobal = globalItems.has(id);
		const visibleGlobalItems = visibleItemMenuIds.filter((id) => globalItems.has(id));
		const visibleAppSpecificItems = visibleItemMenuIds.filter((id) => appSpecificItems[id]);
		const currentGroup = isGlobal ? visibleGlobalItems : visibleAppSpecificItems;
		fireEvent('ui.sideNav.viewed.sideNavSubMenu', {
			navLayoutComponent: 'main',
			group: isGlobal ? 'global' : 'appSpecific',
			groupItemsCount: currentGroup.length,
			groupItemsIndex: currentGroup.findIndex((key) => key === id),
			groupItem: id,
			isInMore,
			groupSubItemCount: subItemsCount ?? null,
		});
	};

	const fireScrollSideNavEvent = (scrollDirection: 'up' | 'down') => {
		fireEvent('ui.sideNav.scrolled.sideNavMenu', {
			scrollDirection,
		});
	};

	const getCustomizationModalAttributes = (
		appSpecificItemsList: HasMenuIdAndVisible[],
		appShortcutItemsList: HasMenuIdAndVisible[],
	) => {
		const checkedItems = [...appSpecificItemsList, ...appShortcutItemsList]
			.filter((item) => item.visible)
			.map((item) => item.menuId);

		return {
			appSpecificItemsCount: appSpecificItemsList.length,
			appSpecificItemsList: appSpecificItemsList.map((item) => item.menuId),
			appShortcutItemsCount: appShortcutItemsList.length,
			appShortcutItemsList: appShortcutItemsList.map((item) => item.menuId),
			isCheckedItemsCount: checkedItems.length,
			isCheckedItemsList: checkedItems,
		};
	};

	const fireViewedCustomizeModalEvent = (
		appSpecificItemsList: HasMenuIdAndVisible[],
		appShortcutItemsList: HasMenuIdAndVisible[],
	) =>
		fireEvent(
			'ui.customizationModal.viewed.customizationModalMenu',
			getCustomizationModalAttributes(appSpecificItemsList, appShortcutItemsList),
		);

	const fireClickCustomizeModalSaveEvent = (
		appSpecificItemsList: HasMenuIdAndVisible[],
		appShortcutItemsList: HasMenuIdAndVisible[],
	) =>
		fireEvent(
			'ui.customizationModal.clicked.customizationModalSave',
			getCustomizationModalAttributes(appSpecificItemsList, appShortcutItemsList),
		);

	const fireClickCustomizeModalCancelEvent = (
		appSpecificItemsList: HasMenuIdAndVisible[],
		appShortcutItemsList: HasMenuIdAndVisible[],
	) =>
		fireEvent(
			'ui.customizationModal.clicked.customizationModalCancel',
			getCustomizationModalAttributes(appSpecificItemsList, appShortcutItemsList),
		);

	const fireViewedShortcutsEvent = (shortcuts: GlobalShortcutsItem[]) => {
		const attributes: SideNavViewedSideNavAppShortcutsSectionAttributesType = {
			appShortcutItemsCount: shortcuts.length,
			appShortcutItemsList: shortcuts.map((item) => item.label),
		};
		fireEvent('ui.sideNav.viewed.sideNavAppShortcutsSection', attributes);
	};

	const fireClickedSideNavSubMenuItemEvent = (
		id: string,
		globalItems: Map<string, NavItemConfig>,
		appSpecificItems: ItemsConfig,
		visibleItemMenuIds: string[],
		subItemsCount?: number,
		isInMore: boolean = false,
	) => {
		const isGlobal = globalItems.has(id);
		const visibleGlobalItems = visibleItemMenuIds.filter((id) => globalItems.has(id));
		const visibleAppSpecificItems = visibleItemMenuIds.filter((id) => appSpecificItems[id]);
		const currentGroup = isGlobal ? visibleGlobalItems : visibleAppSpecificItems;

		fireEvent('ui.sideNav.clicked.sideNavSubItem', {
			navLayoutComponent: 'main',
			group: isGlobal ? 'global' : 'appSpecific',
			groupItemsCount: currentGroup.length,
			groupItemsIndex: currentGroup.findIndex((key) => key === id),
			groupItem: id,
			isInMore,
			groupSubItemCount: subItemsCount ?? null,
			groupSubItemIndex: null,
			groupSubItem: null,
		});
	};

	return {
		fireViewSideNavEvent,
		fireClickSideNavNavItemEvent,
		fireClickSideNavShortcutItemEvent,
		fireClickSideNavMoreMenuItemEvent,
		fireClickSideNavCustomizeSidebarItemEvent,
		fireClickSideNavItemEvent,
		fireScrollSideNavEvent,
		fireViewSideNavSubMenuEvent,
		fireViewedCustomizeModalEvent,
		fireClickCustomizeModalSaveEvent,
		fireClickCustomizeModalCancelEvent,
		fireViewedShortcutsEvent,
		fireClickedSideNavSubMenuItemEvent,
	};
}
